import { nanoid } from 'nanoid'
import * as argon2 from 'argon2'
import { Scope } from './types'

export function generateApiKey(prefix: string = 'kit'): string {
  const randomPart = nanoid(32)
  return `${prefix}_${randomPart}`
}

export async function hashApiKey(key: string): Promise<string> {
  return argon2.hash(key, {
    type: argon2.argon2id,
    timeCost: 2,
    memoryCost: 19456,
    parallelism: 1
  })
}

export async function verifyApiKey(key: string, hash: string): Promise<boolean> {
  try {
    return await argon2.verify(hash, key)
  } catch {
    return false
  }
}

export function parseApiKey(key: string): { prefix: string; lastFour: string } | null {
  const parts = key.split('_')
  if (parts.length < 2) {
    return null
  }
  
  const prefix = parts[0]
  const lastFour = parts[parts.length - 1].slice(-4)
  
  return { prefix, lastFour }
}

export function validateScope(scope: string): boolean {
  // Basic scope validation - alphanumeric, dots, colons, hyphens
  return /^[a-zA-Z0-9._:-]+$/.test(scope)
}

export function normalizeScope(scope: string): string {
  return scope.toLowerCase().trim()
}

export function hasScope(entitlements: Scope[], requiredScope: Scope): boolean {
  return entitlements.includes(requiredScope)
}

export function hasAnyScope(entitlements: Scope[], requiredScopes: Scope[]): boolean {
  return requiredScopes.some(scope => entitlements.includes(scope))
}

export function hasAllScopes(entitlements: Scope[], requiredScopes: Scope[]): boolean {
  return requiredScopes.every(scope => entitlements.includes(scope))
}

export function filterScopesByPrefix(entitlements: Scope[], prefix: string): Scope[] {
  return entitlements.filter(scope => scope.startsWith(prefix))
}

export function getScopeHierarchy(scope: string): string[] {
  const parts = scope.split('.')
  const hierarchy: string[] = []
  
  for (let i = 1; i <= parts.length; i++) {
    hierarchy.push(parts.slice(0, i).join('.'))
  }
  
  return hierarchy
}

export function isSubScope(parentScope: string, childScope: string): boolean {
  return childScope.startsWith(parentScope + '.') || childScope === parentScope
}

export function getParentScope(scope: string): string | null {
  const lastDotIndex = scope.lastIndexOf('.')
  if (lastDotIndex === -1) {
    return null
  }
  return scope.substring(0, lastDotIndex)
}

export function getChildScopes(scope: string, allScopes: Scope[]): Scope[] {
  return allScopes.filter(s => s.startsWith(scope + '.'))
}

export function createScopeMatcher(pattern: string): (scope: string) => boolean {
  // Escape all regex special characters except * and ?
  const escaped = pattern.replace(/[.+^${}()|[\]\\]/g, '\\$&')
  const regexPattern = escaped
    .replace(/\*/g, '.*')
    .replace(/\?/g, '.')

  const regex = new RegExp(`^${regexPattern}$`)

  return (scope: string) => regex.test(scope)
}

export function validateApiKeyFormat(key: string): boolean {
  // API key format: prefix_randomstring
  const parts = key.split('_')
  if (parts.length < 2) {
    return false
  }
  
  const prefix = parts[0]
  const randomPart = parts.slice(1).join('_')
  
  // Prefix should be alphanumeric
  if (!/^[a-zA-Z0-9]+$/.test(prefix)) {
    return false
  }
  
  // Random part should be at least 16 characters
  if (randomPart.length < 16) {
    return false
  }
  
  return true
}

export function maskApiKey(key: string): string {
  const parts = key.split('_')
  if (parts.length < 2) {
    return '***'
  }
  
  const prefix = parts[0]
  const lastFour = parts[parts.length - 1].slice(-4)
  
  return `${prefix}_***${lastFour}`
}

export function generateSessionId(): string {
  return nanoid()
}

export function generateOrganizationId(): string {
  return nanoid()
}

export function generateUserId(): string {
  return nanoid()
}

export function isExpired(expiresAt: Date): boolean {
  return expiresAt < new Date()
}

export function getTimeUntilExpiry(expiresAt: Date): number {
  return Math.max(0, expiresAt.getTime() - Date.now())
}

export function formatDuration(ms: number): string {
  const seconds = Math.floor(ms / 1000)
  const minutes = Math.floor(seconds / 60)
  const hours = Math.floor(minutes / 60)
  const days = Math.floor(hours / 24)
  
  if (days > 0) {
    return `${days}d ${hours % 24}h ${minutes % 60}m`
  } else if (hours > 0) {
    return `${hours}h ${minutes % 60}m`
  } else if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`
  } else {
    return `${seconds}s`
  }
}

export function sanitizeMetadata(metadata: Record<string, any>): Record<string, any> {
  const sanitized: Record<string, any> = {}
  
  for (const [key, value] of Object.entries(metadata)) {
    // Only allow string, number, boolean, and null values
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' || value === null) {
      sanitized[key] = value
    }
  }
  
  return sanitized
}

export function deepMerge<T extends Record<string, any>>(target: T, source: Partial<T>): T {
  const result = { ...target }
  
  for (const [key, value] of Object.entries(source)) {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      result[key as keyof T] = deepMerge(result[key as keyof T] || ({} as T[keyof T]), value)
    } else {
      result[key as keyof T] = value as T[keyof T]
    }
  }
  
  return result
}
